import logger from '@eventbrite/client-logger';
import { gettext } from '@eventbrite/i18n';
import { ToggleGroup, ToggleGroupItem } from '@eventbrite/marmalade';
import { logEvent } from '@eventbrite/statsig';
import React from 'react';
import { useGlobalNavContext } from '../../../../state';
import styles from './AttendeeCreatorToggle.module.scss';

const getAttendeeHref = (serverUrl?: string) => {
    if (!serverUrl) return '/';
    const currentUrl = new URL(serverUrl);
    const url = new URL('/', currentUrl.origin);
    url.search = currentUrl.search;
    return url.href;
};

const getCreatorHref = (serverUrl?: string) => {
    if (!serverUrl) return '/organizer/overview';
    const currentUrl = new URL(serverUrl);
    const url = new URL('/organizer/overview', currentUrl.origin);
    url.search = currentUrl.search;
    return url.href;
};

export enum IntentToggleOptions {
    FIND_EVENTS = 'find-events',
    HOST_EVENTS = 'host-events',
}

interface AttendeeCreatorToggleProps {
    activeOption?: IntentToggleOptions;
}

const METRIC_NAME = 'GlobalNav - Logged out - Click - Intent Toggle';

const trackIntentClick = (dimensions: Record<string, string>) => () => {
    try {
        logEvent(METRIC_NAME, 1, dimensions);
    } catch (error) {
        logger.warn(
            'GlobalNav - Error logging intent toggle click to Statsig.',
        );
    }
};

const doNothing = () => {
    // noop
};

export const AttendeeCreatorToggle = ({
    activeOption = IntentToggleOptions.FIND_EVENTS,
}: AttendeeCreatorToggleProps) => {
    const { env } = useGlobalNavContext();
    const attendeeHref = getAttendeeHref(env?.serverUrl);
    const creatorHref = getCreatorHref(env?.serverUrl);

    const attendeeDimensions = {
        url: attendeeHref,
        activeOption: activeOption.toString(),
        clickedOption: IntentToggleOptions.FIND_EVENTS.toString(),
    };

    const creatorDimensions = {
        url: creatorHref,
        activeOption: activeOption.toString(),
        clickedOption: IntentToggleOptions.HOST_EVENTS.toString(),
    };

    return (
        <div className={styles.toggleContainer}>
            <ToggleGroup
                aria-label={gettext(
                    'Choose between finding or hosting events',
                ).toString()}
                defaultValue={activeOption}
                onValueChange={doNothing}
                isDeselectable={false}
                placeholder={gettext('Choose an option').toString()}
                onPointerEnterCapture={doNothing}
                onPointerLeaveCapture={doNothing}
            >
                <ToggleGroupItem
                    value={IntentToggleOptions.FIND_EVENTS}
                    aria-label={gettext('Find events').toString()}
                >
                    <a
                        href={attendeeHref}
                        className={styles.toggleLink}
                        onClick={trackIntentClick(attendeeDimensions)}
                    >
                        {gettext('Find events')}
                    </a>
                </ToggleGroupItem>
                <ToggleGroupItem
                    value={IntentToggleOptions.HOST_EVENTS}
                    aria-label={gettext('Host events').toString()}
                >
                    <a
                        href={creatorHref}
                        className={styles.toggleLink}
                        onClick={trackIntentClick(creatorDimensions)}
                    >
                        {gettext('Host events')}
                    </a>
                </ToggleGroupItem>
            </ToggleGroup>
        </div>
    );
};
